import type { Todo } from "../../domain/entities.ts";
import { normalizeReminders, shiftAbsoluteReminders } from "./reminders.ts";
import { addDays, dateInTimezone, deterministicOccurrenceId, nextRecurringDate, nextRecurringReminder, recurrenceHasEnded } from "./recurrence.ts";
import { childTasks } from "./task-hierarchy.ts";
import { resolveWallTime, shiftWallTime } from "./task-time.ts";

export interface RecurrenceControllerCapabilities {
  activeRoot(): string | null;
  tasks(): Todo[];
  canEdit(id: string): boolean;
  find(id: string, rootId: string): Promise<Todo | undefined>;
  change(id: string, rootId: string, update: (todo: Todo) => void): Promise<void>;
  changeBatch?(items: Array<{ id: string; rootId: string; mutate(todo: Todo): void }>): Promise<void>;
  mutate(id: string, update: (todo: Todo) => void): Promise<unknown>;
  markLocal(): void;
  render(): Promise<void>;
  sync(): void;
  formatDate(date: string): string;
  toast(message: string): void;
  now(): number;
}

export interface RecurrenceController {
  createNext(completed: Todo): Promise<string | null>;
  skipNext(id: string): Promise<void>;
}

export function createRecurrenceController(capabilities: RecurrenceControllerCapabilities): RecurrenceController {
  return {
    createNext: async completed => {
      const rootId = completed.rootId || capabilities.activeRoot();
      if (!completed.recurrence || !completed.completed || !rootId) return null;
      const seriesId = completed.recurrenceSeriesId || completed.id;
      const occurrence = (completed.recurrenceOccurrence || 0) + 1;
      const occurrenceDate = nextRecurringDate(completed);
      if (recurrenceHasEnded(completed, occurrenceDate, occurrence)) { capabilities.toast("Recurrence finished"); return null; }
      const id = await deterministicOccurrenceId(seriesId, occurrence);
      const existing = await capabilities.find(id, rootId);
      if (existing) {
        if (existing.deleted) { await capabilities.change(id, rootId, todo => { todo.deleted = false; }); return id; }
        return null;
      }
      const reminderAt = nextRecurringReminder(completed, occurrenceDate);
      const baseDate = completed.dueDate || completed.startDate || completed.wallStartAt?.slice(0, 10) || (completed.startAt ? dateInTimezone(new Date(completed.startAt), completed.recurrence.timezone) : null);
      const dayShift = baseDate ? Math.round((new Date(`${occurrenceDate}T12:00:00`).getTime() - new Date(`${baseDate}T12:00:00`).getTime()) / 86_400_000) : 0;
      const shiftTimestamp = (value?: string | null) => value ? new Date(new Date(value).getTime() + dayShift * 86_400_000).toISOString() : null;
      const shiftedSchedule = (task: Todo) => {
        if (task.scheduleMode === "floating" && task.scheduleTimeZone) {
          const wallStartAt = shiftWallTime(task.wallStartAt, dayShift);
          const wallEndAt = shiftWallTime(task.wallEndAt, dayShift);
          return { scheduleMode: "floating" as const, scheduleTimeZone: task.scheduleTimeZone, wallStartAt, wallEndAt, startAt: wallStartAt ? resolveWallTime(wallStartAt, task.scheduleTimeZone) : null, endAt: wallEndAt ? resolveWallTime(wallEndAt, task.scheduleTimeZone) : null };
        }
        return { scheduleMode: task.scheduleMode || "fixed" as const, scheduleTimeZone: null, wallStartAt: null, wallEndAt: null, startAt: shiftTimestamp(task.startAt), endAt: shiftTimestamp(task.endAt) };
      };
      const changes: Array<{ id: string; rootId: string; mutate(todo: Todo): void }> = [{ id, rootId, mutate: todo => {
        todo.title = completed.title;
        todo.description = completed.description;
        todo.type = completed.type;
        if (completed.customFields) todo.customFields = structuredClone(completed.customFields);
        if (completed.customFieldSchema) todo.customFieldSchema = structuredClone(completed.customFieldSchema);
        todo.subtasks = (completed.subtasks || []).map(item => ({ ...item, completed: false }));
        todo.position = capabilities.now();
        todo.priority = completed.priority;
        todo.dueDate = completed.dueDate ? occurrenceDate : null;
        todo.startDate = completed.startDate ? addDays(completed.startDate, dayShift) : completed.dueDate || completed.startAt ? null : occurrenceDate;
        Object.assign(todo, shiftedSchedule(completed));
        todo.estimatedMinutes = completed.estimatedMinutes || null;
        todo.reminderAt = reminderAt;
        todo.reminders = shiftAbsoluteReminders(normalizeReminders(completed), dayShift, completed.scheduleMode === "floating" ? completed.scheduleTimeZone : null);
        todo.recurrence = structuredClone(completed.recurrence!);
        todo.recurrenceSeriesId = seriesId;
        todo.recurrenceOccurrence = occurrence;
        todo.recurrenceAnchorDate = completed.recurrenceAnchorDate || baseDate;
        todo.categoryId = completed.categoryId;
        todo.epicId = completed.epicId || null;
        todo.cycleId = completed.cycleId || null;
        todo.assignedTo = [...(completed.assignedTo || [])];
        todo.tags = { ...(completed.tags || {}) };
        todo.tagOrder = [...(completed.tagOrder || [])];
        todo.parentTaskId = null;
      } }];
      for (const child of childTasks(capabilities.tasks(), completed)) {
        if (!child.completed) {
          changes.push({ id: child.id, rootId, mutate: todo => { todo.parentTaskId = id; } });
          continue;
        }
        const childId = await deterministicOccurrenceId(`${seriesId}:${child.id}`, occurrence);
        changes.push({ id: childId, rootId, mutate: todo => {
          Object.assign(todo, structuredClone(child));
          todo.id = childId;
          todo.parentTaskId = id;
          todo.position = child.position;
          todo.completed = false;
          todo.completedAt = null;
          todo.archivedAt = null;
          todo.approvalEvents = [];
          todo.automationEvents = [];
          todo.recurrence = null;
          todo.recurrenceSeriesId = null;
          todo.subtasks = (child.subtasks || []).map(item => ({ ...item, completed: false }));
          todo.dueDate = child.dueDate ? addDays(child.dueDate, dayShift) : null;
          todo.startDate = child.startDate ? addDays(child.startDate, dayShift) : null;
          Object.assign(todo, shiftedSchedule(child));
          todo.reminders = shiftAbsoluteReminders(normalizeReminders(child), dayShift, child.scheduleMode === "floating" ? child.scheduleTimeZone : null);
          todo.reminderAt = todo.reminders.find(item => item.mode === "absolute")?.at || null;
          todo.comments = [];
          todo.relationships = (child.relationships || []).filter(link => link.type !== "parent");
          todo.createdAt = capabilities.now();
        } });
      }
      if (capabilities.changeBatch) await capabilities.changeBatch(changes);
      else for (const change of changes) await capabilities.change(change.id, change.rootId, change.mutate);
      capabilities.markLocal();
      capabilities.toast(`Next occurrence scheduled for ${capabilities.formatDate(occurrenceDate)}`);
      return id;
    },
    skipNext: async id => {
      const todo = capabilities.tasks().find(item => item.id === id);
      if (!todo?.recurrence || !capabilities.canEdit(id)) return;
      const date = nextRecurringDate(todo);
      await capabilities.mutate(id, draft => {
        if (!draft.recurrence) return;
        draft.recurrence.skippedDates = [...new Set([...(draft.recurrence.skippedDates || []), date])].sort();
      });
      await capabilities.render();
      capabilities.toast(`Skipped ${capabilities.formatDate(date)}`);
      capabilities.sync();
    },
  };
}
